import React, { useState } from 'react';
import { ArrowRightIcon } from '../icons';
import { Modal } from './Modal';
import { apiService } from '../../services/apiService';
import { friendlyAuthError } from '../../utils/authErrors';
import { InlineAlert } from '../InlineAlert';

interface BugReportModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const BugCategoryButton = ({ text, active = false, onClick }: { text: string, active?: boolean, onClick: () => void }) => (
    <button onClick={onClick} className={`py-2.5 px-3 rounded-full ${active ? 'border border-grayscale-1300' : 'bg-grayscale-200'} `}>
        <span className={`font-grotesk text-xs font-medium ${active ? 'text-grayscale-1300' : 'text-grayscale-900'}`}>{text}</span>
    </button>
);

const bugCategories = [
    "Chat responses",
    "Contacting university",
    "Posting a Query",
    "Login issues",
    "Other"
];

export const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose }) => {
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const toggleCategory = (category: string) => {
        setSelectedCategories(prev => 
            prev.includes(category) 
                ? prev.filter(c => c !== category) 
                : [...prev, category]
        );
    };

    const handleSubmitBugReport = async () => {
        if (isSubmitting) return;

        // Basic validation
        if (!description.trim()) {
            setErrorMessage('Please provide a description of the bug.');
            return;
        }

        if (selectedCategories.length === 0) {
            setErrorMessage('Please select at least one area where you\'re facing issues.');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('idle');
        setErrorMessage('');

        try {
            const userId = apiService.getStoredUserId();
            if (!userId) {
                setErrorMessage('User session not found. Please log in again.');
                setIsSubmitting(false);
                return;
            }

            const bugCategory = selectedCategories.join(', ');
            const bugDescription = description.trim();

            const response = await apiService.reportBug(userId, bugCategory, bugDescription);

            if (response.success) {
                setSubmitStatus('success');
                // Reset form and close modal after showing success
                setTimeout(() => {
                    onClose();
                    setDescription('');
                    setSelectedCategories([]);
                    setSubmitStatus('idle');
                }, 2000);
            } else {
                setSubmitStatus('error');
                setErrorMessage(friendlyAuthError(response.error, 'Failed to submit bug report. Please try again.'));
            }
        } catch (error) {
            setSubmitStatus('error');
            setErrorMessage('Network error. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Report a Bug">
            <div className="flex flex-col gap-4">
                <div>
                    <h2 className="text-grayscale-1300 font-grotesk text-base font-bold">Report a bug</h2>
                    <p className="text-grayscale-900 font-grotesk text-xs">Please select area(s) where you are facing an issue</p>
                </div>
                
                <div className="flex flex-wrap gap-2">
                    {bugCategories.map(category => (
                        <BugCategoryButton 
                            key={category} 
                            text={category} 
                            active={selectedCategories.includes(category)} 
                            onClick={() => toggleCategory(category)}
                        />
                    ))}
                </div>
                
                <textarea
                    placeholder="Add message here"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="flex h-24 py-3 px-2.5 items-start gap-2.5 self-stretch rounded-md bg-grayscale-200 text-secondary-text font-grotesk text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />

                {/* Status Messages */}
                {submitStatus === 'success' && (
                    <InlineAlert variant="success" className="mt-4">
                        Bug report submitted successfully! We'll look into it. 🐛
                    </InlineAlert>
                )}
                {(submitStatus === 'error' || errorMessage) && (
                    <InlineAlert variant="error" className="mt-4">{errorMessage}</InlineAlert>
                )}

                <button 
                    onClick={handleSubmitBugReport}
                    disabled={isSubmitting || submitStatus === 'success'}
                    className="flex py-2.5 px-4 justify-center items-center gap-2 self-stretch rounded-full bg-primary-500 text-grayscale-100 font-grotesk text-sm font-medium disabled:bg-primary-300 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? 'Reporting...' : submitStatus === 'success' ? 'Reported!' : 'Report'}
                    {!isSubmitting && <ArrowRightIcon />}
                </button>
            </div>
        </Modal>
    );
};